import { toDisplayString as _toDisplayString, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, unref as _unref, withModifiers as _withModifiers, withKeys as _withKeys, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue";
const _hoisted_1 = {
  class: "w-full"
};
const _hoisted_2 = {
  class: "font-bold mr-1"
};
const _hoisted_3 = {
  key: 0,
  class: "text-xs text-red-500 mt-1"
};
import { computed, ref, watch, onMounted, nextTick } from 'vue';
import { VDateInput } from 'vuetify/labs/VDateInput';
import { getDatesInRange, getMonthsDifference, parseLocalDate } from '@/services/utils.js';
export default {
  __name: 'LlDatePicker',
  props: {
    modelValue: {
      type: [String, Date, Array, null],
      default: null
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    masks: {
      type: Object,
      default: () => ({
        input: 'YYYY-MM-DD'
      })
    },
    disabled: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: 'Date'
    },
    multiple: {
      type: [String, Boolean],
      default: false
    },
    showTextField: {
      type: Boolean,
      default: false
    },
    showButton: {
      type: Boolean,
      default: true
    },
    dataCy: {
      type: String,
      default: ''
    },
    maxRangeInMonths: {
      type: Number,
      default: 24
    },
    showRangeLimitMessage: {
      type: Boolean
    },
    labelClass: {
      type: String,
      required: false
    },
    isRequired: {
      type: Boolean,
      required: false,
      default: false
    },
    min: {
      type: [String, Date],
      default: null
    },
    max: {
      type: [String, Date],
      default: null
    },
    hideDetails: {
      type: [Boolean, String],
      default: false
    }
  },
  emits: ['update:modelValue', 'validation:error'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const dateInputRef = ref(null);
    const isDateRangeMode = computed(() => props.multiple === 'range');
    const isRangeExceedingLimit = ref(false);
    const internalValue = ref(props.modelValue);
    const hasValidationError = ref(false);
    const isManuallyEditing = ref(false);
    const rawInputText = ref('');
    const cursorPosition = ref(null);
    const selectionEnd = ref(null);

    /**
     * Apply the max range limit to a date range
     * Returns the final end date to use (either original or adjusted)
     */
    function applyRangeLimit(startDate, endDate) {
      const monthDiff = getMonthsDifference(startDate, endDate);

      // Set the flag based on whether range exceeds limit
      isRangeExceedingLimit.value = monthDiff > props.maxRangeInMonths;
      if (monthDiff > props.maxRangeInMonths) {
        // If range exceeds limit, adjust end date
        const limitedEndDate = new Date(startDate);
        limitedEndDate.setMonth(startDate.getMonth() + props.maxRangeInMonths);
        return limitedEndDate;
      }
      return endDate;
    }

    // Helper to parse different date formats
    function parseDate(dateStr, forceForValidation = false) {
      if (!dateStr) return null;

      // Don't try to parse obviously incomplete inputs, unless we're validating
      if (!forceForValidation && dateStr.length < 8 && isManuallyEditing.value) {
        return null;
      }

      // Trim the input for consistent parsing
      const trimmedStr = dateStr.trim();

      // Reject strings with non-date characters if we're validating
      if (forceForValidation) {
        // Check for invalid characters in different date formats
        const usFormat = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
        const isoFormat = /^\d{4}-\d{2}-\d{2}$/;
        const euroFormat = /^\d{1,2}\.\d{1,2}\.\d{4}$/;

        // If not matching any valid format exactly, it's invalid
        if (!usFormat.test(trimmedStr) && !isoFormat.test(trimmedStr) && !euroFormat.test(trimmedStr)) {
          return null;
        }
      }

      // Try MM/DD/YYYY format (US)
      if (/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.test(trimmedStr)) {
        const date = new Date(trimmedStr);
        return !isNaN(date.getTime()) ? date : null;
      }

      // Try YYYY-MM-DD format (ISO)
      if (/^(\d{4})-(\d{2})-(\d{2})$/.test(trimmedStr)) {
        const date = new Date(trimmedStr);
        return !isNaN(date.getTime()) ? date : null;
      }

      // Try DD.MM.YYYY format (European)
      if (/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.test(trimmedStr)) {
        const parts = trimmedStr.split('.');
        const date = new Date(`${parts[1]}/${parts[0]}/${parts[2]}`);
        return !isNaN(date.getTime()) ? date : null;
      }

      // Add more lenient parsing for incomplete dates
      if (isManuallyEditing.value || forceForValidation) {
        // For MM/DD format, assume current year ONLY if not actively editing or if forced for validation
        if (/^(\d{1,2})\/(\d{1,2})$/.test(trimmedStr) && (forceForValidation || !rawInputText.value.endsWith('/'))) {
          const now = new Date();
          const fullDateStr = `${trimmedStr}/${now.getFullYear()}`;
          const date = new Date(fullDateStr);
          return !isNaN(date.getTime()) ? date : null;
        }
        // Don't try to parse if we're still in the middle of typing (unless forced for validation)
        if (!forceForValidation) return null;
      }

      // Last resort - try browser's default parsing
      const date = new Date(trimmedStr);
      return !isNaN(date.getTime()) ? date : null;
    }

    /**
     * Handle Enter key press - parse and update the date range
     */
    function handleEnterKey(event) {
      const inputValue = event.target.value;
      const currentInput = event.target;
      if (isDateRangeMode.value && typeof inputValue === 'string' && inputValue.includes('-')) {
        parseAndSetDateRange(inputValue);
      } else {
        const parsedDate = parseDate(inputValue);
        if (parsedDate) {
          internalValue.value = parsedDate;
          emit('update:modelValue', parsedDate);
          hasValidationError.value = false;
          emit('validation:error', false);
        }
      }
      if (currentInput) {
        nextTick(() => {
          currentInput.focus();
        });
      }
    }

    /**
     * Handle focus event - enable manual editing mode
     */
    function onFocus(event) {
      isManuallyEditing.value = true;
      rawInputText.value = event.target?.value || '';
      cursorPosition.value = event.target?.selectionStart || 0;
      selectionEnd.value = event.target?.selectionEnd || 0;
    }

    /**
     * Helper to restore cursor position after DOM updates
     */
    function restoreCursorPosition(element, pos, endPos) {
      if (element && typeof pos === 'number') {
        nextTick(() => {
          if (element.setSelectionRange) {
            try {
              element.setSelectionRange(pos, endPos || pos);
              element.focus();
            } catch (e) {
              console.error('Could not restore cursor position:', e);
            }
          }
        });
      }
    }

    /**
     * Handle clear button click
     */
    function onClear() {
      internalValue.value = null;
      emit('update:modelValue', null);
      hasValidationError.value = false;
      emit('validation:error', false);
    }
    function onInput(event) {
      if (isManuallyEditing.value) {
        const inputValue = event.target?.value || '';
        const currentCursorPos = event.target?.selectionStart || 0;
        const currentSelectionEnd = event.target?.selectionEnd || 0;
        cursorPosition.value = currentCursorPos;
        selectionEnd.value = currentSelectionEnd;
        rawInputText.value = inputValue;

        // For date range mode: preserve cursor position but don't update automatically
        // Require Enter key or blur event to apply changes
        if (isDateRangeMode.value && inputValue.includes('-')) {
          restoreCursorPosition(event.target, currentCursorPos, currentSelectionEnd);
          return;
        }

        // For single date mode: process input in real-time
        if (!isDateRangeMode.value) {
          const isIncompleteInput = inputValue.endsWith('/') || inputValue.includes('/') && inputValue.length < 10 || !inputValue.includes('/') && inputValue.length > 0 && inputValue.length < 8;

          // Don't process incomplete inputs
          if (isIncompleteInput) {
            return;
          }

          // For complete looking single dates, try to parse and update in real-time
          if (inputValue.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
            const parsedDate = parseDate(inputValue);
            if (parsedDate) {
              internalValue.value = parsedDate;
              emit('update:modelValue', parsedDate);
              hasValidationError.value = false;
              emit('validation:error', false);

              // Restore cursor position after update
              nextTick(() => {
                restoreCursorPosition(event.target, currentCursorPos, currentSelectionEnd);
              });
            } else {
              // Invalid but complete-looking date
              hasValidationError.value = true;
              emit('validation:error', true);
            }
          } else if (!inputValue) {
            // Empty input - clear the value
            internalValue.value = null;
            emit('update:modelValue', null);
            hasValidationError.value = false;
            emit('validation:error', false);
          }
        }
      }
    }
    function onBlur(event) {
      const inputValue = event.target?.value;
      if (!inputValue) {
        internalValue.value = null;
        emit('update:modelValue', null);
        hasValidationError.value = false;
        emit('validation:error', false);
        isManuallyEditing.value = false;
        return;
      }
      if (isDateRangeMode.value && inputValue.includes('-')) {
        parseAndSetDateRange(inputValue);
      } else {
        const parsedDate = parseDate(inputValue, true);
        if (parsedDate) {
          internalValue.value = parsedDate;
          emit('update:modelValue', parsedDate);
          hasValidationError.value = false;
          emit('validation:error', false);
        } else {
          hasValidationError.value = true;
          emit('validation:error', true);
        }
      }
      isManuallyEditing.value = false;
    }

    /**
     * Handle changes to input value - only process when not manually editing
     */
    function handleInputChange(value) {
      if (isManuallyEditing.value) return;
      if (isDateRangeMode.value && typeof value === 'string' && value.includes('-')) {
        parseAndSetDateRange(value);
      }
      nextTick(() => {
        checkValidation(dateValue.value);
      });
    }

    /**
     * Parse and set date range from an input string
     */
    function parseAndSetDateRange(inputValue) {
      const parts = inputValue.split('-').map(s => s.trim());
      if (parts.length !== 2) return;
      const startDate = parseDate(parts[0], true);
      const endDate = parseDate(parts[1], true);
      if (startDate && endDate) {
        const activeElement = document.activeElement;
        const savedCursorPos = cursorPosition.value;
        const savedSelectionEnd = selectionEnd.value;
        const finalEndDate = applyRangeLimit(startDate, endDate);
        const allDatesInRange = getDatesInRange(startDate, finalEndDate);
        internalValue.value = allDatesInRange;
        emit('update:modelValue', internalValue.value);
        setTimeout(() => {
          emit('update:modelValue', [...internalValue.value]);
          if (activeElement && activeElement === dateInputRef.value.$el.querySelector('input')) {
            restoreCursorPosition(activeElement, savedCursorPos, savedSelectionEnd);
          }
        }, 0);
        nextTick(() => {
          checkValidation(allDatesInRange);
        });
      } else {
        hasValidationError.value = true;
        emit('validation:error', true);
      }
    }

    /**
     * 1) A helper to validate a single "MM/DD/YYYY" date string
     *    (month/day can be 1 or 2 digits).
     */
    function validateSingleDate(str) {
      const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
      if (!dateRegex.test(str.trim())) {
        return 'Invalid date format. Use MM/DD/YYYY.';
      }
      const parsedDate = parseDate(str, true);
      if (!parsedDate) {
        return 'Invalid date value.';
      }
      const [_, month, day, year] = str.trim().match(dateRegex);
      const monthNum = parseInt(month, 10);
      const dayNum = parseInt(day, 10);
      const yearNum = parseInt(year, 10);
      if (monthNum < 1 || monthNum > 12) {
        return 'Month must be between 1 and 12.';
      }
      const daysInMonth = [31, yearNum % 4 === 0 && yearNum % 100 !== 0 || yearNum % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (dayNum < 1 || dayNum > daysInMonth[monthNum - 1]) {
        return `Invalid day for the month ${monthNum}. Max is ${daysInMonth[monthNum - 1]}.`;
      }
      return true;
    }

    /**
     * 2) Main validator that can handle:
     *    - Array of dates (multiple/range)
     *    - A single date
     *    - A single string with a dash ("03/12/2025 - 03/20/2025")
     */
    function validateDateString(str) {
      if (!str) return true;
      if (str instanceof Date) {
        str = str.toLocaleDateString('en-US');
      }
      if (typeof str === 'string' && str.includes('-')) {
        const parts = str.split('-').map(s => s.trim());
        if (parts.length !== 2) {
          return 'Invalid range format.';
        }
        for (const piece of parts) {
          const res = validateSingleDate(piece);
          if (res !== true) return res;
        }
        return true;
      }
      return validateSingleDate(str);
    }
    const dateRules = [value => {
      if (props.isRequired && !value) {
        return 'Date is required.';
      }
      if (!value) return true;
      if (Array.isArray(value)) {
        if (!value.length) return true;
        for (const item of value) {
          const res = validateDateString(item);
          if (res !== true) return res;
        }
        return true;
      }
      return validateDateString(value);
    }];
    const allRules = computed(() => {
      if (isDateRangeMode.value) {
        return [...dateRules, validateDateRange];
      }
      return dateRules;
    });
    function validateDateRange(value) {
      if (!value || !Array.isArray(value) || value.length < 2) return true;
      const startDate = value[0] instanceof Date ? value[0] : new Date(value[0]);
      const endDate = value[value.length - 1] instanceof Date ? value[value.length - 1] : new Date(value[value.length - 1]);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return true;
      const monthDiff = getMonthsDifference(startDate, endDate);
      isRangeExceedingLimit.value = monthDiff > props.maxRangeInMonths;
      return monthDiff <= props.maxRangeInMonths || `Date range cannot exceed ${props.maxRangeInMonths / 12} years`;
    }
    function checkValidation(value) {
      hasValidationError.value = false;
      if (!value) {
        emit('validation:error', false);
        return true;
      }
      for (const rule of allRules.value) {
        const result = rule(value);
        if (result !== true) {
          hasValidationError.value = true;
          emit('validation:error', true);
          return false;
        }
      }
      emit('validation:error', false);
      return true;
    }
    const dateValue = computed({
      get() {
        if (props.multiple === 'range' || props.multiple === 'true') {
          return Array.isArray(internalValue.value) ? internalValue.value : internalValue.value ? [internalValue.value] : [];
        }
        if (!internalValue.value || internalValue.value instanceof Date) {
          return internalValue.value;
        }
        // A plain "YYYY-MM-DD" string must be parsed as local midnight, otherwise
        // new Date() treats it as UTC and the picker shows the previous day in
        // timezones behind UTC.
        return parseLocalDate(internalValue.value);
      },
      set(newValue) {
        if (isDateRangeMode.value && typeof newValue === 'string' && newValue.includes('-')) {
          parseAndSetDateRange(newValue);
          return;
        }
        if (!isDateRangeMode.value && typeof newValue === 'string' && !newValue.includes('-')) {
          const parsedDate = parseDate(newValue);
          if (parsedDate) {
            internalValue.value = parsedDate;
            return;
          }
        }
        if (isDateRangeMode.value && Array.isArray(newValue) && newValue.length >= 2) {
          const startDate = new Date(newValue[0]);
          const endDate = new Date(newValue[newValue.length - 1]);
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            const finalEndDate = applyRangeLimit(startDate, endDate);
            if (finalEndDate !== endDate) {
              internalValue.value = getDatesInRange(startDate, finalEndDate);
              return;
            }
          }
        }
        internalValue.value = newValue;
      }
    });
    watch(() => props.modelValue, newVal => {
      internalValue.value = newVal;
    });
    watch(internalValue, val => {
      emit('update:modelValue', val);
    });
    watch(dateValue, newValue => {
      nextTick(() => {
        checkValidation(newValue);
      });
    }, {
      immediate: true
    });
    function focus() {
      const inputEl = dateInputRef.value?.$el?.querySelector('input');
      if (inputEl && typeof inputEl.focus === 'function') {
        inputEl.focus();
      }
    }
    onMounted(() => {
      if (props.autofocus) {
        nextTick(() => setTimeout(() => focus(), 0));
      }
    });
    __expose({
      focus
    });
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createElementVNode("label", {
        class: _normalizeClass(["pr-2 text-sm", __props.labelClass])
      }, [_createTextVNode(_toDisplayString(__props.label) + " ", 1), _withDirectives(_createElementVNode("span", _hoisted_2, "*", 512), [[_vShow, __props.isRequired]])], 2), _createVNode(_unref(VDateInput), {
        "data-cy": __props.dataCy,
        ref_key: "dateInputRef",
        ref: dateInputRef,
        class: "custom-date-input border-none cursor-pointer remove-outline",
        "append-icon": '',
        "append-inner-icon": '$calendar',
        modelValue: dateValue.value,
        "onUpdate:modelValue": [_cache[0] || (_cache[0] = $event => dateValue.value = $event), handleInputChange],
        variant: "outlined",
        disabled: __props.disabled,
        min: __props.min,
        max: __props.max,
        density: "compact",
        "weeks-in-month": 'dynamic',
        "hide-actions": true,
        rules: allRules.value,
        multiple: __props.multiple,
        readonly: false,
        immediate: false,
        "active-picker": isManuallyEditing.value ? 'input' : undefined,
        onKeydown: _withKeys(_withModifiers(handleEnterKey, ["prevent"]), ["enter"]),
        onFocus: onFocus,
        onBlur: onBlur,
        "onClick:clear": onClear,
        onInput: onInput,
        error: hasValidationError.value,
        "aria-required": __props.isRequired,
        "hide-details": __props.hideDetails,
        color: "primary"
      }, null, 8, ["data-cy", "modelValue", "disabled", "min", "max", "rules", "multiple", "active-picker", "onKeydown", "error", "aria-required", "hide-details"]), isDateRangeMode.value && __props.showRangeLimitMessage && isRangeExceedingLimit.value ? (_openBlock(), _createElementBlock("div", _hoisted_3, " Date range cannot exceed " + _toDisplayString(__props.maxRangeInMonths / 12) + " years ", 1)) : _createCommentVNode("", true)]);
    };
  }
};